<template>
  <div class="gym-tracker-container gym-root" :data-gym-base="theme.base" :style="themeStyle">

    <!-- ── SETUP WIZARD ── -->
    <div v-if="appState === 'setup'" class="centered-screen">
      <div class="setup-card">
        <div class="setup-header">
          <h2>GymTracker – Inställningar</h2>
          <p class="setup-intro">
            Ange URL:en till din GymTracker API.<br>
            <strong>Lokal körning</strong> (dev-server med HTTPS):
            <code>https://localhost:3000/gymapi/graphql</code><br>
            <strong>Server</strong>:
            <code>https://gymapi.brasse-pc.eu/graphql</code>
          </p>
        </div>
        <div class="input-group">
          <label for="setup-url">API-URL (GraphQL-ändpunkt)</label>
          <input id="setup-url" type="text" v-model="setupUrl"
            placeholder="https://gymapi.brasse-pc.eu/graphql" class="url-input"
            @keyup.enter="handleTestSetup" />
        </div>
        <div class="setup-actions">
          <button @click="handleTestSetup" :disabled="setupTesting || !setupUrl.trim()" class="btn-secondary">
            {{ setupTesting ? 'Testar...' : 'Testa anslutning' }}
          </button>
          <button @click="handleSaveSetup" :disabled="!setupTestPassed" class="btn-primary">
            Spara och fortsätt
          </button>
        </div>
        <div v-if="setupTestResult" :class="['test-result', setupTestResult.corsWarning ? 'warn' : setupTestResult.ok ? 'ok' : 'fail']">
          {{ setupTestResult.corsWarning ? '⚠' : setupTestResult.ok ? '✓' : '✗' }}
          {{ setupTestResult.message }}
        </div>
        <button v-if="apiUrl" @click="showSetup = false" class="btn-link cancel-btn">Avbryt</button>
      </div>
    </div>

    <!-- ── CONNECTING ── -->
    <div v-else-if="appState === 'connecting'" class="centered-screen">
      <div class="status-card">
        <div class="spinner"></div>
        <p>Ansluter till API...</p>
        <code class="url-badge">{{ apiUrl }}</code>
      </div>
    </div>

    <!-- ── DISCONNECTED ── -->
    <div v-else-if="appState === 'disconnected'" class="centered-screen">
      <div class="status-card error-card">
        <h3>Kan inte nå API</h3>
        <p>Försöker nå:</p>
        <code class="url-badge">{{ apiUrl }}</code>
        <p class="hint-text">Kontrollera att API:et är igång och att adressen är korrekt.</p>
        <div class="setup-actions">
          <button @click="retryConnection" class="btn-secondary">Försök igen</button>
          <button @click="openSetup" class="btn-primary">Ändra API-URL</button>
        </div>
      </div>
    </div>

    <!-- ── LOGIN ── -->
    <div v-else-if="appState === 'login'" class="centered-screen">
      <form @submit.prevent="handleLogin" class="login-card">
        <h2>Logga in</h2>
        <div class="api-connected-badge">
          Ansluten till: <code>{{ apiUrl }}</code>
          <button type="button" @click="openSetup" class="badge-btn">Ändra</button>
        </div>
        <div class="input-group">
          <label for="username">Användarnamn</label>
          <input id="username" type="text" v-model="username" required autocomplete="username" />
        </div>
        <div class="input-group">
          <label for="password">Lösenord</label>
          <input id="password" type="password" v-model="password" required autocomplete="current-password" />
        </div>
        <p v-if="gymAuthState.error" class="login-error">{{ gymAuthState.error }}</p>
        <button type="submit" :disabled="gymAuthState.loading" class="btn-primary full-width">
          {{ gymAuthState.loading ? 'Loggar in...' : 'Logga in' }}
        </button>
      </form>
    </div>

    <!-- ── DASHBOARD ── -->
    <div v-else-if="appState === 'dashboard'" class="dashboard">

      <header class="dash-header">
        <div class="dash-brand">
          <span class="dash-logo">🏋️</span>
          <span class="dash-title">GymTracker</span>
        </div>

        <button v-if="activeSession" class="active-pass-pill" @click="resumeSession" title="Återuppta pågående pass">
          <span class="pulse-dot"></span>
          <span class="pill-name">{{ activeSession.name }}</span>
          <span class="pill-time">{{ formatDuration(activeElapsed) }}</span>
        </button>

        <div class="dash-actions">
          <span class="dash-user">{{ profile?.displayName || gymAuthState.username }}</span>
          <button @click="openTheme" class="btn-icon" title="Tema">🎨</button>
          <button @click="openSetup" class="btn-icon" title="API-inställningar">⚙</button>
          <button @click="handleLogout" class="btn-outline-sm">Logga ut</button>
        </div>
      </header>

      <!-- Desktop top navigation -->
      <nav class="tab-nav">
        <button v-for="tab in tabs" :key="tab.id"
          @click="switchTab(tab.id)"
          :class="['tab-btn', { active: activeTab === tab.id }]">
          <span class="tab-btn-icon">{{ tab.icon }}</span>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <transition name="tab" mode="out-in">
      <div class="tab-content" :key="activeTab">

      <!-- ── TAB: PASS ── -->
      <div v-if="activeTab === 'pass'" class="tab-pane">
        <GymSessionTab
          :exercise-types="exerciseTypes"
          :body-weight-kg="bodyWeightKg"
          @toast="onChildToast"
          @lifts-changed="onLiftStatusChanged"
          @refresh="onSessionRefresh"
        />
      </div>

      <!-- ── TAB: AKTIVITETER ── -->
      <div v-if="activeTab === 'aktivitet'" class="tab-pane">
        <ActivitiesTab @toast="onChildToast" />
      </div>

      <!-- ── TAB: LOGGA LYFT ── -->
      <div v-if="activeTab === 'lifts'" class="tab-pane">
        <div class="card lift-form-card">
          <h3>Logga ett lyft</h3>
          <form @submit.prevent="handleCreateLift">
            <div class="input-group">
              <label for="lift-exercise">Övning</label>
              <select id="lift-exercise" v-model="newLift.exerciseTypeId" required>
                <option :value="null" disabled>Välj en övning...</option>
                <option v-for="et in exerciseTypes" :key="et.id" :value="et.id">{{ et.name }}</option>
              </select>
            </div>
            <div class="input-row">
              <div class="input-group">
                <label for="lift-weight">Vikt (kg)</label>
                <input id="lift-weight" type="number" step="0.5" v-model.number="newLift.weight" required />
              </div>
              <div class="input-group">
                <label for="lift-reps">Reps</label>
                <input id="lift-reps" type="number" v-model.number="newLift.reps" required />
              </div>
              <div class="input-group">
                <label for="lift-sets">Sets</label>
                <input id="lift-sets" type="number" v-model.number="newLift.sets" />
              </div>
            </div>
            <div class="input-row">
              <div class="input-group">
                <label for="lift-date">Datum</label>
                <input id="lift-date" type="date" v-model="newLift.date" />
              </div>
              <div class="input-group flex-2">
                <label for="lift-notes">Notering</label>
                <input id="lift-notes" type="text" v-model="newLift.notes" placeholder="Valfri anteckning" />
              </div>
            </div>
            <button type="submit" class="btn-primary">Logga lyft</button>
          </form>
        </div>

        <div class="card">
          <h3>Mina lyft</h3>
          <div v-if="myLifts.length === 0" class="empty-state">Inga lyft loggade än.</div>
          <ul v-else class="lift-list">
            <li v-for="lift in myLifts" :key="lift.id" class="lift-item">
              <div class="lift-left">
                <strong class="lift-exercise">{{ lift.exerciseType.name }}</strong>
                <span class="lift-stats">
                  {{ lift.weight }} kg &times; {{ lift.reps }} reps
                  <span v-if="lift.sets > 1">&nbsp;({{ lift.sets }} sets)</span>
                </span>
                <span v-if="lift.notes" class="lift-notes">{{ lift.notes }}</span>
                <span v-if="lift.status && lift.status !== 'training'" class="lift-status" :class="lift.status">
                  {{ lift.status === 'competition' ? '🎖️ Tävling' : '⛔ Räknas ej' }}<span v-if="lift.statusNote"> · {{ lift.statusNote }}</span>
                </span>
              </div>
              <div class="lift-right">
                <span class="lift-date">{{ formatDate(lift.date) }}</span>
                <button @click="openLiftStatus(lift)" class="btn-icon-sm" title="Rekordstatus (träning/tävling/räknas ej)">🏅</button>
                <button @click="handleDeleteLift(lift.id)" class="btn-danger-sm" title="Ta bort">✕</button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- ── TAB: PB LISTA ── -->
      <div v-if="activeTab === 'pb'" class="tab-pane">
        <GymPbTab ref="pbTabRef" @changed="onLiftStatusChanged" @open-session="openSessionFromPb" />
      </div>

      <LiftStatusSheet v-if="liftSheet" :context="liftSheet" @close="liftSheet = null" @changed="onLiftStatusChanged" @open-session="openSessionFromPb" />

      <!-- ── TAB: STATISTIK ── -->
      <div v-if="activeTab === 'statistik'" class="tab-pane">
        <GymStatsTab ref="statsTabRef" />
      </div>

      <!-- ── TAB: IMPORTERA ── -->
      <div v-if="activeTab === 'import'" class="tab-pane">
        <GymImportTab :exerciseTypes="exerciseTypes" :muscleGroups="muscleGroups" :muscles="muscles"
                      @imported="handleImportDone" @goto="switchTab" />
      </div>

      <!-- ── TAB: GRUNDDATA ── -->
      <div v-if="activeTab === 'grunddata'" class="tab-pane grunddata-pane">

        <!-- Muskelgrupper -->
        <div class="card">
          <h3>Muskelgrupper</h3>
          <form @submit.prevent="handleCreateMuscleGroup" class="inline-form">
            <input type="text" v-model="newMuscleGroup.name" placeholder="Namn på muskelgrupp" required />
            <button type="submit" class="btn-primary">Lägg till</button>
          </form>
          <ul class="data-list">
            <li v-if="muscleGroups.length === 0" class="empty-item">Inga muskelgrupper ännu.</li>
            <li v-for="mg in muscleGroups" :key="mg.id" class="data-item">
              <template v-if="editingMgId === mg.id">
                <input v-model="editMgName" class="edit-input"
                  @keyup.enter="saveEditMuscleGroup(mg.id)"
                  @keyup.escape="editingMgId = null" />
                <div class="action-btns">
                  <button @click="saveEditMuscleGroup(mg.id)" class="btn-save">✓</button>
                  <button @click="editingMgId = null" class="btn-cancel">✕</button>
                </div>
              </template>
              <template v-else>
                <span>{{ mg.name }}</span>
                <div class="action-btns">
                  <button @click="startEditMuscleGroup(mg)" class="btn-edit" title="Redigera">✏</button>
                  <button @click="handleDeleteMuscleGroup(mg.id)" class="btn-delete" title="Ta bort">🗑</button>
                </div>
              </template>
            </li>
          </ul>
        </div>

        <!-- Muskler -->
        <div class="card">
          <h3>Muskler</h3>
          <form @submit.prevent="handleCreateMuscle" class="stacked-form">
            <input type="text" v-model="newMuscle.name" placeholder="Namn på muskel" required />
            <select v-model="newMuscle.muscleGroupId">
              <option :value="null">(Ingen grupp)</option>
              <option v-for="mg in muscleGroups" :key="mg.id" :value="mg.id">{{ mg.name }}</option>
            </select>
            <button type="submit" class="btn-primary">Lägg till</button>
          </form>
          <ul class="data-list">
            <li v-if="muscles.length === 0" class="empty-item">Inga muskler ännu.</li>
            <li v-for="m in muscles" :key="m.id" class="data-item">
              <template v-if="editingMuscleId === m.id">
                <div class="edit-row">
                  <input v-model="editMuscle.name" class="edit-input" @keyup.escape="editingMuscleId = null" />
                  <select v-model="editMuscle.muscleGroupId" class="edit-select">
                    <option :value="null">(Ingen grupp)</option>
                    <option v-for="mg in muscleGroups" :key="mg.id" :value="mg.id">{{ mg.name }}</option>
                  </select>
                </div>
                <div class="action-btns">
                  <button @click="saveEditMuscle(m.id)" class="btn-save">✓</button>
                  <button @click="editingMuscleId = null" class="btn-cancel">✕</button>
                </div>
              </template>
              <template v-else>
                <span>
                  {{ m.name }}
                  <span v-if="m.muscleGroupId" class="tag">{{ getMuscleGroupName(m.muscleGroupId) }}</span>
                </span>
                <div class="action-btns">
                  <button @click="startEditMuscle(m)" class="btn-edit" title="Redigera">✏</button>
                  <button @click="handleDeleteMuscle(m.id)" class="btn-delete" title="Ta bort">🗑</button>
                </div>
              </template>
            </li>
          </ul>
        </div>

        <!-- Övningstyper -->
        <div class="card">
          <h3>Övningstyper</h3>
          <form @submit.prevent="handleCreateExerciseType" class="stacked-form">
            <input type="text" v-model="newExerciseType.name" placeholder="Namn på övning" required />
            <textarea v-model="newExerciseType.description" placeholder="Beskrivning (valfritt)" rows="2"></textarea>
            <div class="met-row">
              <label class="small-label">MET (kalori-intensitet)</label>
              <input type="number" step="0.1" min="1" v-model.number="newExerciseType.metValue" class="met-input" />
            </div>
            <div class="input-group">
              <label class="small-label">Mätningstyp</label>
              <div class="flags-box">
                <label class="flag-label"><input type="checkbox" v-model="newExerciseType.tracksWeight" /> Vikt</label>
                <label class="flag-label"><input type="checkbox" v-model="newExerciseType.tracksReps" /> Reps</label>
                <label class="flag-label"><input type="checkbox" v-model="newExerciseType.tracksDistance" /> Distans</label>
                <label class="flag-label"><input type="checkbox" v-model="newExerciseType.tracksDuration" /> Tid</label>
                <label class="flag-label"><input type="checkbox" v-model="newExerciseType.isBodyweight" /> Kroppsvikt</label>
              </div>
            </div>
            <div class="input-group">
              <label class="small-label">Kopplade muskler</label>
              <div class="multi-select-box">
                <span v-if="muscles.length === 0" class="empty-item">Lägg till muskler först.</span>
                <label v-for="m in muscles" :key="m.id" class="checkbox-label">
                  <input type="checkbox" :value="m.id" v-model="newExerciseType.muscleIds" />
                  {{ m.name }}
                </label>
              </div>
            </div>
            <button type="submit" class="btn-primary">Lägg till</button>
          </form>
          <ul class="data-list">
            <li v-if="exerciseTypes.length === 0" class="empty-item">Inga övningstyper ännu.</li>
            <li v-for="et in exerciseTypes" :key="et.id" class="data-item">
              <template v-if="editingEtId === et.id">
                <div class="edit-col">
                  <input v-model="editEt.name" class="edit-input" @keyup.escape="editingEtId = null" />
                  <input v-model="editEt.description" class="edit-input" placeholder="Beskrivning" />
                  <div class="met-row">
                    <label class="small-label">MET</label>
                    <input type="number" step="0.1" min="1" v-model.number="editEt.metValue" class="met-input" />
                  </div>
                  <div class="flags-box">
                    <label class="flag-label"><input type="checkbox" v-model="editEt.tracksWeight" /> Vikt</label>
                    <label class="flag-label"><input type="checkbox" v-model="editEt.tracksReps" /> Reps</label>
                    <label class="flag-label"><input type="checkbox" v-model="editEt.tracksDistance" /> Distans</label>
                    <label class="flag-label"><input type="checkbox" v-model="editEt.tracksDuration" /> Tid</label>
                    <label class="flag-label"><input type="checkbox" v-model="editEt.isBodyweight" /> Kroppsvikt</label>
                  </div>
                  <div class="multi-select-box multi-select-sm">
                    <label v-for="m in muscles" :key="m.id" class="checkbox-label">
                      <input type="checkbox" :value="m.id" v-model="editEt.muscleIds" />
                      {{ m.name }}
                    </label>
                  </div>
                </div>
                <div class="action-btns">
                  <button @click="saveEditExerciseType(et.id)" class="btn-save">✓</button>
                  <button @click="editingEtId = null" class="btn-cancel">✕</button>
                </div>
              </template>
              <template v-else>
                <span>{{ et.name }}</span>
                <div class="action-btns">
                  <button @click="startEditExerciseType(et)" class="btn-edit" title="Redigera">✏</button>
                  <button @click="handleDeleteExerciseType(et.id)" class="btn-delete" title="Ta bort">🗑</button>
                </div>
              </template>
            </li>
          </ul>
        </div>

      </div>

      <!-- ── TAB: PROFIL ── -->
      <div v-if="activeTab === 'profil'" class="tab-pane">
        <div class="card">
          <h3>Profil</h3>
          <div class="profile-row">
            <span class="profile-label">Användare</span>
            <span class="profile-value">{{ profile?.displayName || gymAuthState.username }}</span>
          </div>
          <div class="profile-row" v-if="profile?.email">
            <span class="profile-label">E-post</span>
            <span class="profile-value">{{ profile.email }}</span>
          </div>
          <div class="input-group" style="margin-top: 1rem;">
            <label>Kroppsvikt &amp; sammansättning</label>
            <p class="hint-text" style="margin:0 0 0.5rem;">
              Vikten används för kaloriuppskattning i pass. Muskel/fett/vatten är valfria.
            </p>
            <div class="bm-form">
              <input type="date" v-model="bmDate" class="bw-input" />
              <input type="number" step="0.1" inputmode="decimal" v-model="bmWeight"
                     placeholder="Vikt (kg)" class="bw-input" />
              <input type="number" step="0.1" inputmode="decimal" v-model="bmMuscle"
                     placeholder="Muskel %" class="bw-input" />
              <input type="number" step="0.1" inputmode="decimal" v-model="bmFat"
                     placeholder="Fett %" class="bw-input" />
              <input type="number" step="0.1" inputmode="decimal" v-model="bmWater"
                     placeholder="Vatten %" class="bw-input" />
              <button class="btn-primary" :disabled="savingBodyWeight || !bmWeight" @click="saveMeasurement">
                {{ savingBodyWeight ? 'Sparar...' : (editingMeasurementId ? 'Uppdatera' : 'Lägg till') }}
              </button>
              <button v-if="editingMeasurementId" class="btn-secondary" @click="resetMeasurementForm">Avbryt</button>
            </div>
          </div>

          <BodyMeasurementChart v-if="measurements.length" :measurements="measurements" style="margin-top:1rem;" />

          <div class="input-group" style="margin-top: 1rem;">
            <label>Kroppsdata</label>
            <p class="hint-text" style="margin:0 0 0.5rem;">
              Längd, födelseår och kön används för BMR-baserad kaloriberäkning
              och av smarta vågar (kroppssammansättning).
            </p>
            <div class="bm-form">
              <input type="number" step="0.5" inputmode="decimal" v-model="heightInput"
                     placeholder="Längd (cm)" class="bw-input" />
              <input type="number" step="1" inputmode="numeric" v-model="birthYearInput"
                     placeholder="Födelseår" class="bw-input" />
              <select v-model="sexInput" class="bw-input">
                <option value="">Kön (ej satt)</option>
                <option value="male">Man</option>
                <option value="female">Kvinna</option>
              </select>
              <button class="btn-primary" :disabled="savingBodyData" @click="saveBodyData">
                {{ savingBodyData ? 'Sparar...' : 'Spara kroppsdata' }}
              </button>
            </div>
          </div>

          <div v-if="measurements.length" class="bm-history">
            <h4 style="margin:1rem 0 0.5rem;">Historik</h4>
            <table class="bm-table">
              <thead>
                <tr><th>Datum</th><th>Vikt</th><th>Muskel</th><th>Fett</th><th>Vatten</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="m in measurements" :key="m.id">
                  <td>{{ (m.date || '').slice(0, 10) }}</td>
                  <td>{{ m.weightKg }} kg</td>
                  <td>{{ m.musclePercent != null ? m.musclePercent + '%' : '–' }}</td>
                  <td>{{ m.fatPercent != null ? m.fatPercent + '%' : '–' }}</td>
                  <td>{{ m.waterPercent != null ? m.waterPercent + '%' : '–' }}</td>
                  <td class="bm-actions">
                    <button class="btn-link" @click="editMeasurement(m)">Ändra</button>
                    <button class="btn-link btn-danger" @click="removeMeasurement(m.id)">Ta bort</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      </div>
      </transition>

      <!-- Mobile bottom navigation -->
      <nav class="bottom-nav">
        <button v-for="t in bottomNav" :key="t.id"
          @click="pickTab(t.id)"
          :class="['bn-item', { active: activeTab === t.id }]">
          <span class="bn-icon">{{ t.icon }}</span>
          <span class="bn-label">{{ t.short }}</span>
        </button>
        <button @click="openMore" :class="['bn-item', { active: moreActive || moreOpen }]">
          <span class="bn-icon">⋯</span>
          <span class="bn-label">Mer</span>
        </button>
      </nav>

      <!-- "Mer" sheet -->
      <BottomSheet v-if="moreOpen" title="Mer" @close="closeMore">
        <div class="sheet-user">
          <span class="su-avatar">{{ (profile?.displayName || gymAuthState.username || '?').charAt(0).toUpperCase() }}</span>
          <div class="su-meta">
            <span class="su-name">{{ profile?.displayName || gymAuthState.username }}</span>
            <span class="su-sub">{{ activeSession ? 'Pågående pass · ' + formatDuration(activeElapsed) : 'Inget pågående pass' }}</span>
          </div>
        </div>

        <div class="sheet-group">
          <button v-for="t in moreTabs" :key="t.id"
            @click="pickTab(t.id)"
            :class="['sheet-row', { active: activeTab === t.id }]">
            <span class="sr-icon">{{ t.icon }}</span>
            <span class="sr-label">{{ t.label }}</span>
            <span v-if="activeTab === t.id" class="sr-dot"></span>
          </button>
        </div>

        <div class="sheet-group">
          <button @click="openThemeFromMore" class="sheet-row">
            <span class="sr-icon">🎨</span><span class="sr-label">Tema</span>
            <span class="sr-trail">{{ themeMode === 'dark' ? 'Mörkt' : themeMode === 'light' ? 'Ljust' : 'Eget' }}</span>
          </button>
          <button @click="openSetupFromMore" class="sheet-row">
            <span class="sr-icon">⚙</span><span class="sr-label">API-inställningar</span>
          </button>
          <button @click="logoutFromMore" class="sheet-row danger">
            <span class="sr-icon">⎋</span><span class="sr-label">Logga ut</span>
          </button>
        </div>
      </BottomSheet>

      <!-- Theme sheet -->
      <BottomSheet v-if="themeOpen" title="Tema" @close="closeTheme">
        <div class="theme-modes">
          <button :class="['theme-mode', { active: themeMode === 'dark' }]" @click="applyPreset('dark')">
            <span class="tm-swatch tm-dark"></span><span>Mörkt</span>
          </button>
          <button :class="['theme-mode', { active: themeMode === 'light' }]" @click="applyPreset('light')">
            <span class="tm-swatch tm-light"></span><span>Ljust</span>
          </button>
          <button :class="['theme-mode', { active: themeMode === 'custom' }]" @click="applyPreset('custom')">
            <span class="tm-swatch tm-custom"></span><span>Eget</span>
          </button>
        </div>

        <div class="theme-custom">
          <div class="tc-row">
            <span class="tc-label">Bas</span>
            <div class="tc-seg">
              <button :class="{ active: theme.base === 'dark' }" @click="setBase('dark')">Mörkt</button>
              <button :class="{ active: theme.base === 'light' }" @click="setBase('light')">Ljust</button>
            </div>
          </div>
          <div class="tc-row">
            <span class="tc-label">Accentfärg</span>
            <label class="tc-color">
              <input type="color" :value="theme.accent" @input="onColorInput" />
              <span class="tc-color-val">{{ theme.accent }}</span>
            </label>
          </div>
          <div class="tc-swatches">
            <button v-for="p in accentPresets" :key="p.value"
              :class="['tc-swatch', { active: theme.accent === p.value }]"
              :style="{ background: p.value }" :title="p.name"
              @click="setAccent(p.value)"></button>
          </div>
        </div>

        <div class="theme-preview">
          <span class="tp-label">Förhandsvisning</span>
          <div class="tp-row">
            <button class="btn-primary">Primär</button>
            <button class="btn-secondary">Sekundär</button>
            <span class="tp-pill"><span class="pulse-dot"></span> Pass</span>
          </div>
        </div>
      </BottomSheet>

      <transition name="toast">
        <div v-if="statusMessage" :class="['status-toast', { 'toast-error': statusIsError }]">
          {{ statusMessage }}
        </div>
      </transition>
    </div>

  </div>
</template>

<script>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { gymAuthState, login, logout, fetchMyLifts, refreshIfNeeded, restoreSession } from '../services/gymTrackerAuth.js';
import api, { getApiUrl, saveApiUrl, testApiUrl } from '../services/gymTrackerApi.js';
import { sessionStore, loadActiveSession, loadProfile, setProfile } from '../services/gymSessionStore.js';
import { formatDuration, numOrNull } from '../services/gymHelpers.js';
import {
  theme, themeMode, themeStyle, ACCENT_PRESETS,
  loadTheme, setBase, setAccent, applyPreset,
} from '../services/gymTheme.js';
import GymStatsTab from '../components/GymStatsTab.vue';
import GymImportTab from '../components/gym/import/GymImportTab.vue';
import GymSessionTab from '../components/gym/GymSessionTab.vue';
import ActivitiesTab from '../components/gym/ActivitiesTab.vue';
import BottomSheet from '../components/gym/BottomSheet.vue';
import BodyMeasurementChart from '../components/gym/BodyMeasurementChart.vue';
import GymPbTab from '../components/gym/GymPbTab.vue';
import LiftStatusSheet from '../components/gym/LiftStatusSheet.vue';

export default {
  name: 'GymTrackerView',
  components: { GymStatsTab, GymImportTab, GymSessionTab, ActivitiesTab, BottomSheet, BodyMeasurementChart, GymPbTab, LiftStatusSheet },
  setup() {
    // ── API config ──
    const apiUrl = ref(getApiUrl());
    const showSetup = ref(!apiUrl.value);
    const connectionChecked = ref(false);
    const connectionOk = ref(false);

    const setupUrl = ref(apiUrl.value || 'https://gymapi.brasse-pc.eu/graphql');
    const setupTesting = ref(false);
    const setupTestResult = ref(null);
    const setupTestPassed = computed(() =>
      setupTestResult.value?.ok === true || setupTestResult.value?.corsWarning === true);

    const appState = computed(() => {
      if (showSetup.value || !apiUrl.value) return 'setup';
      if (!connectionChecked.value) return 'connecting';
      if (!connectionOk.value) return 'disconnected';
      if (!gymAuthState.loggedIn) return 'login';
      return 'dashboard';
    });

    // ── Tabs ──
    const activeTab = ref('lifts');
    // PB-fliken laddar sig själv; pbDirty säger att den ska ladda om nästa gång den visas.
    const pbDirty = ref(true);
    const pbTabRef = ref(null);
    const liftSheet = ref(null);
    const statsTabRef = ref(null);
    const tabs = [
      { id: 'pass',       label: 'Pass',       short: 'Pass',  icon: '🏋️' },
      { id: 'lifts',      label: 'Logga lyft', short: 'Lyft',  icon: '📝' },
      { id: 'aktivitet',  label: 'Aktiviteter', short: 'Aktiv', icon: '❤️' },
      { id: 'statistik',  label: 'Statistik',  short: 'Stat',  icon: '📊' },
      { id: 'pb',         label: 'PB Lista',   short: 'PB',    icon: '🏆' },
      { id: 'import',     label: 'Importera',  short: 'Import', icon: '📥' },
      { id: 'grunddata',  label: 'Grunddata',  short: 'Data',  icon: '🗂️' },
      { id: 'profil',     label: 'Profil',     short: 'Profil', icon: '👤' },
    ];
    // Mobile bottom-nav shows these four; everything else lives in the "Mer" sheet.
    const PRIMARY = ['pass', 'lifts', 'statistik', 'pb'];
    const byId = id => tabs.find(t => t.id === id);
    const bottomNav = PRIMARY.map(byId);
    const moreTabs = tabs.filter(t => !PRIMARY.includes(t.id));
    const moreActive = computed(() => !PRIMARY.includes(activeTab.value));

    async function switchTab(id) {
      activeTab.value = id;
      if (id === 'pb' && pbDirty.value) {
        await nextTick();
        pbTabRef.value?.reload();
        pbDirty.value = false;
      }
    }

    // Rekordstatus (träning/tävling/räknas ej) på ett löst lyft från "Mina lyft".
    function openLiftStatus(lift) {
      liftSheet.value = { exerciseTypeId: lift.exerciseType?.id, exerciseTypeName: lift.exerciseType?.name, reps: lift.reps, liftId: lift.id };
    }
    async function onLiftStatusChanged() {
      pbDirty.value = activeTab.value !== 'pb';
      reloadStats();
      try { myLifts.value = await fetchMyLifts(); } catch { /* behåll listan */ }
    }
    function openSessionFromPb() { liftSheet.value = null; switchTab('pass'); }

    // ── Mobile sheets (Mer menu + theme panel) ──
    const moreOpen = ref(false);
    const themeOpen = ref(false);
    function openMore() { moreOpen.value = true; }
    function closeMore() { moreOpen.value = false; }
    function openTheme() { themeOpen.value = true; }
    function closeTheme() { themeOpen.value = false; }
    function pickTab(id) { switchTab(id); closeMore(); }
    function openThemeFromMore() { moreOpen.value = false; themeOpen.value = true; }
    function openSetupFromMore() { moreOpen.value = false; openSetup(); }
    function logoutFromMore() { moreOpen.value = false; handleLogout(); }
    function onColorInput(e) { setAccent(e.target.value); }

    // ── Active-session header indicator (ticking timer) ──
    const nowMs = ref(Date.now());
    let headerTicker = null;
    const activeSession = computed(() => sessionStore.active);
    const activeElapsed = computed(() => {
      const s = sessionStore.active;
      if (!s?.startTime) return 0;
      return Math.max(0, Math.floor((nowMs.value - new Date(s.startTime).getTime()) / 1000));
    });

    // ── Profile (body weight) ──
    const profile = computed(() => sessionStore.profile);
    const bodyWeightKg = computed(() => sessionStore.profile?.bodyWeightKg ?? null);
    const bodyWeightInput = ref('');
    const savingBodyWeight = ref(false);

    async function saveBodyWeight() {
      savingBodyWeight.value = true;
      try {
        const kg = bodyWeightInput.value === '' ? null : Number(bodyWeightInput.value);
        const d = await withToken(t => api.updateBodyWeight(kg, t));
        if (sessionStore.profile) sessionStore.profile.bodyWeightKg = d.updateBodyWeight.bodyWeightKg;
        setStatus('Kroppsvikt sparad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
      finally { savingBodyWeight.value = false; }
    }

    // ── Kroppsdata (längd/födelseår/kön för BMR & smarta vågar) ──
    const heightInput = ref('');
    const birthYearInput = ref('');
    const sexInput = ref('');
    const savingBodyData = ref(false);

    async function saveBodyData() {
      savingBodyData.value = true;
      try {
        // Tomt fält = rensa på servern (-1/"" är serverns rensa-konvention)
        const d = await withToken(t => api.updateProfile({
          heightCm: heightInput.value === '' ? -1 : Number(heightInput.value),
          birthYear: birthYearInput.value === '' ? -1 : Number(birthYearInput.value),
          sex: sexInput.value,
        }, t));
        if (sessionStore.profile) {
          sessionStore.profile.heightCm = d.updateProfile.heightCm;
          sessionStore.profile.birthYear = d.updateProfile.birthYear;
          sessionStore.profile.sex = d.updateProfile.sex;
        }
        setStatus('Kroppsdata sparad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
      finally { savingBodyData.value = false; }
    }

    // ── Body measurements (vikt + kroppssammansättning) ──
    const measurements = ref([]);
    const bmDate = ref(new Date().toISOString().slice(0, 10));
    const bmWeight = ref('');
    const bmMuscle = ref('');
    const bmFat = ref('');
    const bmWater = ref('');
    const editingMeasurementId = ref(null);

    function resetMeasurementForm() {
      editingMeasurementId.value = null;
      bmDate.value = new Date().toISOString().slice(0, 10);
      bmWeight.value = ''; bmMuscle.value = ''; bmFat.value = ''; bmWater.value = '';
    }

    async function loadMeasurements() {
      try {
        const d = await withToken(t => api.getBodyMeasurements(t));
        // senaste först i tabellen
        measurements.value = [...(d.bodyMeasurements || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      } catch (e) { /* äldre API utan body measurements — tyst */ }
    }

    async function saveMeasurement() {
      savingBodyWeight.value = true;
      try {
        const iso = new Date(`${bmDate.value}T12:00:00Z`).toISOString();
        const base = {
          weightKg: Number(bmWeight.value),
          date: iso,
          musclePercent: numOrNull(bmMuscle.value),
          fatPercent: numOrNull(bmFat.value),
          waterPercent: numOrNull(bmWater.value),
        };
        if (editingMeasurementId.value) {
          // -1 rensar ett tömt procentfält på servern
          const clr = v => (v == null ? -1 : v);
          await withToken(t => api.updateBodyMeasurement({
            id: editingMeasurementId.value,
            weightKg: base.weightKg,
            date: iso,
            musclePercent: clr(base.musclePercent),
            fatPercent: clr(base.fatPercent),
            waterPercent: clr(base.waterPercent),
          }, t));
        } else {
          await withToken(t => api.addBodyMeasurement(base, t));
        }
        // Profilens vikt speglar senaste mätningen på servern — hämta om
        if (sessionStore.profile) {
          const p = await withToken(t => api.getProfile(t));
          sessionStore.profile.bodyWeightKg = p.myProfile.bodyWeightKg;
        }
        resetMeasurementForm();
        await loadMeasurements();
        setStatus('Mätning sparad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
      finally { savingBodyWeight.value = false; }
    }

    function editMeasurement(m) {
      editingMeasurementId.value = m.id;
      bmDate.value = (m.date || '').slice(0, 10);
      bmWeight.value = m.weightKg ?? '';
      bmMuscle.value = m.musclePercent ?? '';
      bmFat.value = m.fatPercent ?? '';
      bmWater.value = m.waterPercent ?? '';
    }

    async function removeMeasurement(id) {
      try {
        await withToken(t => api.deleteBodyMeasurement(id, t));
        if (editingMeasurementId.value === id) resetMeasurementForm();
        await loadMeasurements();
        setStatus('Mätning borttagen');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    function onSessionRefresh() {
      fetchMyLifts().then(l => { myLifts.value = l; });
      pbDirty.value = true;
      reloadStats();
    }
    function onChildToast(p) { setStatus(p.msg, p.error); }
    function resumeSession() { switchTab('pass'); }

    // Reload stats when a lift is logged/deleted
    function reloadStats() {
      if (statsTabRef.value) statsTabRef.value.reload();
    }

    // Efter import: ladda om data i bakgrunden men stanna på importfliken (resultatvyn).
    async function handleImportDone() {
      await fetchAllData();
      reloadStats();
    }

    // ── Data ──
    const muscleGroups = ref([]);
    const muscles = ref([]);
    const exerciseTypes = ref([]);
    const myLifts = ref([]);

    const newMuscleGroup = reactive({ name: '' });
    const newMuscle = reactive({ name: '', muscleGroupId: null });
    const blankExerciseType = () => ({
      name: '', description: '', muscleIds: [], metValue: 5.0,
      tracksWeight: true, tracksReps: true, tracksDistance: false, tracksDuration: false, isBodyweight: false,
    });
    const newExerciseType = reactive(blankExerciseType());
    const newLift = reactive({
      exerciseTypeId: null, weight: null, reps: null, sets: 1, notes: '',
      date: new Date().toISOString().split('T')[0],
    });

    // ── Edit state ──
    const editingMgId = ref(null);
    const editMgName = ref('');
    const editingMuscleId = ref(null);
    const editMuscle = reactive({ name: '', muscleGroupId: null });
    const editingEtId = ref(null);
    const editEt = reactive({
      name: '', description: '', muscleIds: [], metValue: 5.0,
      tracksWeight: true, tracksReps: true, tracksDistance: false, tracksDuration: false, isBodyweight: false,
    });

    // ── Status toast ──
    const statusMessage = ref('');
    const statusIsError = ref(false);
    let statusTimer = null;

    function setStatus(msg, isError = false) {
      clearTimeout(statusTimer);
      statusMessage.value = msg;
      statusIsError.value = isError;
      if (msg) statusTimer = setTimeout(() => { statusMessage.value = ''; }, isError ? 6000 : 3000);
    }

    // ── Login form ──
    const username = ref('');
    const password = ref('');

    // ── Token helper ──
    async function withToken(fn) {
      await refreshIfNeeded();
      return fn(gymAuthState.token);
    }

    // ── Connection ──
    async function checkConnection() {
      connectionChecked.value = false;
      connectionOk.value = false;
      const result = await testApiUrl(apiUrl.value);
      connectionOk.value = result.ok;
      connectionChecked.value = true;
    }

    async function handleTestSetup() {
      setupTesting.value = true;
      setupTestResult.value = null;
      setupTestResult.value = await testApiUrl(setupUrl.value);
      setupTesting.value = false;
    }

    async function handleSaveSetup() {
      let url = setupUrl.value.trim().replace(/\/$/, '');
      if (!url.endsWith('/graphql')) url += '/graphql';
      saveApiUrl(url);
      apiUrl.value = url;
      showSetup.value = false;
      await checkConnection();
      if (connectionOk.value) await restoreSession();
    }

    function openSetup() {
      setupUrl.value = apiUrl.value || 'https://gymapi.brasse-pc.eu/graphql';
      setupTestResult.value = null;
      showSetup.value = true;
    }

    async function retryConnection() {
      await checkConnection();
      if (connectionOk.value) await restoreSession();
    }

    // ── Data fetch ──
    async function fetchAllData() {
      if (!gymAuthState.loggedIn) return;
      try {
        const [mgData, mData, etData, liftsData] = await Promise.all([
          withToken(t => api.getMuscleGroups(t)),
          withToken(t => api.getMuscles(t)),
          withToken(t => api.getExerciseTypes(t)),
          fetchMyLifts(),
        ]);
        muscleGroups.value = mgData.muscleGroups || [];
        muscles.value = mData.muscles || [];
        exerciseTypes.value = etData.exerciseTypes || [];
        myLifts.value = liftsData || [];
        pbDirty.value = true;
        // Load profile + any ongoing session for the header indicator / Pass tab.
        await Promise.all([loadProfile(), loadActiveSession()]);
        bodyWeightInput.value = sessionStore.profile?.bodyWeightKg ?? '';
        heightInput.value = sessionStore.profile?.heightCm ?? '';
        birthYearInput.value = sessionStore.profile?.birthYear ?? '';
        sexInput.value = sessionStore.profile?.sex ?? '';
        loadMeasurements();
      } catch (e) {
        setStatus(`Fel vid hämtning: ${e.message}`, true);
      }
    }


    watch(() => gymAuthState.loggedIn, (loggedIn) => {
      if (loggedIn) { loadTheme(gymAuthState.username); fetchAllData(); }
      else {
        muscleGroups.value = [];
        muscles.value = [];
        exerciseTypes.value = [];
        myLifts.value = [];
        pbDirty.value = true;
        sessionStore.active = null;
        setProfile(null);
      }
    }, { immediate: true });

    // ── Auth ──
    async function handleLogin() {
      const ok = await login(username.value, password.value);
      if (!ok) setStatus(gymAuthState.error || 'Inloggning misslyckades', true);
    }

    async function handleLogout() { await logout(); }

    // ── Lift ──
    async function handleCreateLift() {
      try {
        setStatus('Loggar lyft...');
        // Empty number inputs arrive as "" — coerce so GraphQL Int/Float accepts them.
        await withToken(t => api.createLift({
          ...newLift,
          weight: numOrNull(newLift.weight),
          reps: numOrNull(newLift.reps),
          sets: numOrNull(newLift.sets),
        }, t));
        Object.assign(newLift, {
          exerciseTypeId: null, weight: null, reps: null, sets: 1, notes: '',
          date: new Date().toISOString().split('T')[0],
        });
        myLifts.value = await fetchMyLifts();
        pbDirty.value = true;
        reloadStats();
        setStatus('Lyft loggat!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    async function handleDeleteLift(id) {
      if (!confirm('Ta bort detta lyft?')) return;
      try {
        await withToken(t => api.deleteLift(id, t));
        myLifts.value = myLifts.value.filter(l => l.id !== id);
        pbDirty.value = true;
        reloadStats();
        setStatus('Lyft borttaget.');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    // ── MuscleGroup ──
    async function handleCreateMuscleGroup() {
      try {
        setStatus('Skapar...');
        await withToken(t => api.createMuscleGroup(newMuscleGroup.name, t));
        newMuscleGroup.name = '';
        const d = await withToken(t => api.getMuscleGroups(t));
        muscleGroups.value = d.muscleGroups || [];
        setStatus('Muskelgrupp skapad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    function startEditMuscleGroup(mg) {
      editingMgId.value = mg.id;
      editMgName.value = mg.name;
    }

    async function saveEditMuscleGroup(id) {
      try {
        await withToken(t => api.updateMuscleGroup(id, editMgName.value, t));
        editingMgId.value = null;
        const d = await withToken(t => api.getMuscleGroups(t));
        muscleGroups.value = d.muscleGroups || [];
        setStatus('Muskelgrupp uppdaterad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    async function handleDeleteMuscleGroup(id) {
      if (!confirm('Ta bort muskelgruppen?')) return;
      try {
        await withToken(t => api.deleteMuscleGroup(id, t));
        muscleGroups.value = muscleGroups.value.filter(mg => mg.id !== id);
        setStatus('Muskelgrupp borttagen.');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    // ── Muscle ──
    async function handleCreateMuscle() {
      try {
        setStatus('Skapar...');
        await withToken(t => api.createMuscle(newMuscle.name, newMuscle.muscleGroupId, t));
        newMuscle.name = '';
        newMuscle.muscleGroupId = null;
        const d = await withToken(t => api.getMuscles(t));
        muscles.value = d.muscles || [];
        setStatus('Muskel skapad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    function startEditMuscle(m) {
      editingMuscleId.value = m.id;
      editMuscle.name = m.name;
      editMuscle.muscleGroupId = m.muscleGroupId;
    }

    async function saveEditMuscle(id) {
      try {
        await withToken(t => api.updateMuscle(id, editMuscle.name, editMuscle.muscleGroupId, t));
        editingMuscleId.value = null;
        const d = await withToken(t => api.getMuscles(t));
        muscles.value = d.muscles || [];
        setStatus('Muskel uppdaterad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    async function handleDeleteMuscle(id) {
      if (!confirm('Ta bort muskeln?')) return;
      try {
        await withToken(t => api.deleteMuscle(id, t));
        muscles.value = muscles.value.filter(m => m.id !== id);
        setStatus('Muskel borttagen.');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    // ── ExerciseType ──
    function exerciseTypeInput(src) {
      return {
        name: src.name,
        description: src.description || null,
        muscleIds: src.muscleIds,
        metValue: src.metValue,
        tracksWeight: src.tracksWeight,
        tracksReps: src.tracksReps,
        tracksDistance: src.tracksDistance,
        tracksDuration: src.tracksDuration,
        isBodyweight: src.isBodyweight,
      };
    }

    async function handleCreateExerciseType() {
      try {
        setStatus('Skapar...');
        await withToken(t => api.createExerciseType(exerciseTypeInput(newExerciseType), t));
        Object.assign(newExerciseType, blankExerciseType());
        const d = await withToken(t => api.getExerciseTypes(t));
        exerciseTypes.value = d.exerciseTypes || [];
        setStatus('Övningstyp skapad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    function startEditExerciseType(et) {
      editingEtId.value = et.id;
      editEt.name = et.name;
      editEt.description = et.description || '';
      editEt.muscleIds = (et.exerciseTypeMuscles || []).map(etm => etm.muscle.id);
      editEt.metValue = et.metValue ?? 5.0;
      editEt.tracksWeight = et.tracksWeight ?? true;
      editEt.tracksReps = et.tracksReps ?? true;
      editEt.tracksDistance = et.tracksDistance ?? false;
      editEt.tracksDuration = et.tracksDuration ?? false;
      editEt.isBodyweight = et.isBodyweight ?? false;
    }

    async function saveEditExerciseType(id) {
      try {
        await withToken(t => api.updateExerciseType({ id, ...exerciseTypeInput(editEt) }, t));
        editingEtId.value = null;
        const d = await withToken(t => api.getExerciseTypes(t));
        exerciseTypes.value = d.exerciseTypes || [];
        setStatus('Övningstyp uppdaterad!');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    async function handleDeleteExerciseType(id) {
      if (!confirm('Ta bort övningstypen?')) return;
      try {
        await withToken(t => api.deleteExerciseType(id, t));
        exerciseTypes.value = exerciseTypes.value.filter(et => et.id !== id);
        setStatus('Övningstyp borttagen.');
      } catch (e) { setStatus(`Fel: ${e.message}`, true); }
    }

    // ── Helpers ──
    function getMuscleGroupName(id) {
      return muscleGroups.value.find(mg => mg.id === id)?.name || '';
    }

    function formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('sv-SE');
    }

    // ── Init ──
    onMounted(async () => {
      loadTheme(gymAuthState.username || null);
      if (apiUrl.value) await checkConnection();
      headerTicker = setInterval(() => { nowMs.value = Date.now(); }, 1000);
    });
    onBeforeUnmount(() => { if (headerTicker) clearInterval(headerTicker); });

    return {
      appState, activeTab, tabs, switchTab, statsTabRef, reloadStats, handleImportDone,
      pbTabRef, liftSheet, openLiftStatus, onLiftStatusChanged, openSessionFromPb,
      bottomNav, moreTabs, moreActive,
      moreOpen, themeOpen, openMore, closeMore, openTheme, closeTheme, pickTab,
      openThemeFromMore, openSetupFromMore, logoutFromMore,
      theme, themeMode, themeStyle, accentPresets: ACCENT_PRESETS,
      setBase, setAccent, applyPreset, onColorInput,
      activeSession, activeElapsed, resumeSession, formatDuration,
      profile, bodyWeightKg, bodyWeightInput, savingBodyWeight, saveBodyWeight,
      heightInput, birthYearInput, sexInput, savingBodyData, saveBodyData,
      measurements, bmDate, bmWeight, bmMuscle, bmFat, bmWater, editingMeasurementId,
      saveMeasurement, editMeasurement, removeMeasurement, resetMeasurementForm,
      onSessionRefresh, onChildToast,
      apiUrl, showSetup, setupUrl, setupTesting, setupTestResult, setupTestPassed,
      openSetup, handleTestSetup, handleSaveSetup, retryConnection,
      gymAuthState, username, password, handleLogin, handleLogout,
      muscleGroups, muscles, exerciseTypes, myLifts,
      newMuscleGroup, newMuscle, newExerciseType, newLift,
      editingMgId, editMgName, startEditMuscleGroup, saveEditMuscleGroup,
      editingMuscleId, editMuscle, startEditMuscle, saveEditMuscle,
      editingEtId, editEt, startEditExerciseType, saveEditExerciseType,
      handleCreateMuscleGroup, handleDeleteMuscleGroup,
      handleCreateMuscle, handleDeleteMuscle,
      handleCreateExerciseType, handleDeleteExerciseType,
      handleCreateLift, handleDeleteLift,
      statusMessage, statusIsError,
      getMuscleGroupName, formatDate,
    };
  },
};
</script>

<style scoped>
/* ── Container & layout ───────────────────────────────────────────── */
.gym-tracker-container {
  font-family: inherit;
  background: var(--gym-bg-grad);
  color: var(--gym-text);
  min-height: calc(100vh - var(--navbar-height, 70px));
  padding: 0.9rem 0.9rem calc(var(--gym-bottomnav-h) + 1rem);
}
.dashboard { display: flex; flex-direction: column; max-width: 1060px; margin: 0 auto; }

.centered-screen {
  display: flex; align-items: center; justify-content: center;
  min-height: calc(100vh - var(--navbar-height, 70px) - 4rem); padding: 1rem;
}

/* ── Cards / panels ───────────────────────────────────────────────── */
.setup-card, .status-card, .login-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-lg);
  padding: 1.75rem; width: 100%; max-width: 460px;
  box-shadow: var(--gym-shadow);
}
.setup-card h2, .login-card h2 { margin: 0 0 0.5rem; font-size: 1.4rem; color: var(--gym-text); }
.setup-intro { color: var(--gym-text-muted); font-size: 0.9rem; margin: 0 0 1.5rem; line-height: 1.6; }
.setup-intro code, .url-badge {
  background: var(--gym-surface-2); color: var(--gym-text); padding: 0.15rem 0.45rem;
  border-radius: 6px; font-size: 0.82rem; word-break: break-all;
}
.error-card { border-color: var(--gym-danger); }
.error-card h3 { color: var(--gym-danger); margin-top: 0; }

.setup-actions { display: flex; gap: 0.75rem; margin-top: 1rem; flex-wrap: wrap; }

.test-result { margin-top: 0.75rem; font-size: 0.9rem; padding: 0.55rem 0.8rem; border-radius: var(--gym-radius-sm); border: 1px solid transparent; }
.test-result.ok   { background: var(--gym-success-soft); color: var(--gym-success); border-color: var(--gym-success); }
.test-result.warn { background: var(--gym-warning-soft); color: var(--gym-warning); border-color: var(--gym-warning); }
.test-result.fail { background: var(--gym-danger-soft);  color: var(--gym-danger);  border-color: var(--gym-danger); }

.cancel-btn { display: block; margin-top: 1rem; text-align: center; }
.url-badge { display: inline-block; margin: 0.4rem 0; }
.hint-text { color: var(--gym-text-muted); font-size: 0.88rem; margin: 0.5rem 0 1rem; line-height: 1.55; }

.spinner {
  width: 34px; height: 34px; border: 3px solid var(--gym-border);
  border-top-color: var(--gym-accent); border-radius: 50%;
  animation: spin 0.8s linear infinite; margin: 0 auto 1rem;
}
@keyframes spin { to { transform: rotate(360deg); } }
.status-card { text-align: center; color: var(--gym-text-muted); }

.api-connected-badge {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.8rem; color: var(--gym-text-muted); background: var(--gym-success-soft);
  border: 1px solid color-mix(in srgb, var(--gym-success) 40%, transparent); border-radius: var(--gym-radius-sm);
  padding: 0.45rem 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;
}
.api-connected-badge code { color: var(--gym-success); font-size: 0.78rem; word-break: break-all; }
.badge-btn {
  margin-left: auto; background: none; border: none;
  color: var(--gym-accent); cursor: pointer; font-size: 0.8rem; padding: 0; text-decoration: underline;
}
.login-error { color: var(--gym-danger); font-size: 0.9em; margin: 0.4rem 0; }

/* ── Dashboard header ─────────────────────────────────────────────── */
.dash-header {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.4rem 0 1rem; border-bottom: 1px solid var(--gym-border); margin-bottom: 1.25rem;
}
.dash-brand { display: flex; align-items: center; gap: 0.5rem; margin-right: auto; }
.dash-logo {
  font-size: 1.2rem; width: 38px; height: 38px; flex-shrink: 0;
  display: grid; place-items: center; border-radius: 11px;
  background: var(--gym-accent-soft); border: 1px solid var(--gym-accent-border);
}
.dash-title {
  font-size: 1.3rem; font-weight: 800; letter-spacing: -0.02em;
  color: var(--gym-text);
}
.dash-actions { display: flex; align-items: center; gap: 0.6rem; }
.dash-user { font-size: 0.9rem; color: var(--gym-text); font-weight: 600; }

.active-pass-pill {
  display: inline-flex; align-items: center; gap: 0.45rem; max-width: 52vw;
  background: var(--gym-success-soft); border: 1px solid color-mix(in srgb, var(--gym-success) 45%, transparent);
  color: var(--gym-success);
  border-radius: var(--gym-radius-pill); padding: 0.3rem 0.7rem; font-size: 0.78rem; font-weight: 700;
  cursor: pointer; font-variant-numeric: tabular-nums; transition: filter var(--gym-dur) var(--gym-ease);
}
.active-pass-pill:hover { filter: brightness(1.1); }
.pill-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pill-time { opacity: 0.85; }
.pulse-dot {
  width: 8px; height: 8px; border-radius: 50%; background: var(--gym-success); flex-shrink: 0;
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.82); } }

/* ── Desktop tab nav ──────────────────────────────────────────────── */
.tab-nav {
  display: flex; gap: 0.25rem; flex-wrap: wrap;
  background: var(--gym-surface); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius); padding: 0.35rem; margin-bottom: 1.5rem;
}
.tab-btn {
  display: inline-flex; align-items: center; gap: 0.45rem;
  background: none; border: none; border-radius: var(--gym-radius-sm);
  padding: 0.55rem 0.95rem; font-size: 0.92rem; cursor: pointer; color: var(--gym-text-muted);
  transition: color var(--gym-dur) var(--gym-ease), background var(--gym-dur) var(--gym-ease);
}
.tab-btn-icon { font-size: 1rem; }
.tab-btn:hover { color: var(--gym-text); background: var(--gym-surface-2); }
.tab-btn.active { color: var(--gym-on-accent); background: var(--gym-accent); font-weight: 600; box-shadow: var(--gym-shadow-sm); }

/* ── Tab content + transition ─────────────────────────────────────── */
.tab-content { will-change: opacity, transform; }
.tab-pane { display: flex; flex-direction: column; gap: 1.25rem; }
.tab-enter-active, .tab-leave-active { transition: opacity 0.22s var(--gym-ease), transform 0.22s var(--gym-ease); }
.tab-enter-from { opacity: 0; transform: translateY(10px); }
.tab-leave-to { opacity: 0; transform: translateY(-6px); }

.grunddata-pane {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem; align-items: start;
}

/* ── Mobile bottom navigation ─────────────────────────────────────── */
.bottom-nav { display: none; }

/* ── Sheet content (slotted into BottomSheet) ─────────────────────── */
.sheet-user { display: flex; align-items: center; gap: 0.75rem; }
.su-avatar {
  width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
  display: grid; place-items: center; font-weight: 800; font-size: 1.05rem;
  background: var(--gym-accent); color: var(--gym-on-accent);
}
.su-meta { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.su-name { font-weight: 700; color: var(--gym-text); }
.su-sub { font-size: 0.78rem; color: var(--gym-text-muted); }

.sheet-group {
  display: flex; flex-direction: column;
  background: var(--gym-surface-2); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius); overflow: hidden;
}
.sheet-row {
  display: flex; align-items: center; gap: 0.75rem; width: 100%;
  background: none; border: none; border-bottom: 1px solid var(--gym-border);
  padding: 0.85rem 0.95rem; font-size: 0.95rem; color: var(--gym-text);
  cursor: pointer; text-align: left; transition: background var(--gym-dur) var(--gym-ease);
}
.sheet-row:last-child { border-bottom: none; }
.sheet-row:hover { background: var(--gym-surface-3); }
.sheet-row.active { background: var(--gym-accent-soft); color: var(--gym-accent); font-weight: 600; }
.sheet-row.danger { color: var(--gym-danger); }
.sr-icon { font-size: 1.15rem; width: 1.5rem; text-align: center; }
.sr-label { flex: 1; }
.sr-trail { font-size: 0.82rem; color: var(--gym-text-muted); }
.sr-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--gym-accent); }

/* ── Theme sheet ──────────────────────────────────────────────────── */
.theme-modes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.6rem; }
.theme-mode {
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
  background: var(--gym-surface-2); border: 2px solid var(--gym-border);
  border-radius: var(--gym-radius); padding: 0.85rem 0.5rem;
  font-size: 0.85rem; font-weight: 600; color: var(--gym-text); cursor: pointer;
  transition: border-color var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease);
}
.theme-mode:active { transform: scale(0.97); }
.theme-mode.active { border-color: var(--gym-accent); }
.tm-swatch { width: 42px; height: 42px; border-radius: 11px; border: 1px solid var(--gym-border-strong); }
.tm-dark { background: linear-gradient(135deg, #1e2835, #0e131b); }
.tm-light { background: linear-gradient(135deg, #ffffff, #dbe3ee); }
.tm-custom { background: conic-gradient(from 210deg, #ef4444, #f59e0b, #22c55e, #3b82f6, #a855f7, #ef4444); }

.theme-custom {
  display: flex; flex-direction: column; gap: 0.85rem;
  background: var(--gym-surface-2); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius); padding: 0.95rem;
}
.tc-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.tc-label { font-size: 0.88rem; font-weight: 600; color: var(--gym-text); }
.tc-seg { display: inline-flex; background: var(--gym-surface-3); border-radius: var(--gym-radius-sm); padding: 0.2rem; }
.tc-seg button {
  background: none; border: none; padding: 0.4rem 0.85rem; border-radius: 7px;
  font-size: 0.85rem; color: var(--gym-text-muted); cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease);
}
.tc-seg button.active { background: var(--gym-accent); color: var(--gym-on-accent); font-weight: 600; }
.tc-color { display: inline-flex; align-items: center; gap: 0.5rem; cursor: pointer; }
.tc-color input[type="color"] {
  width: 40px; height: 32px; padding: 0; border: 1px solid var(--gym-border-strong);
  border-radius: 8px; background: none; cursor: pointer;
}
.tc-color-val { font-size: 0.82rem; color: var(--gym-text-muted); font-variant-numeric: tabular-nums; text-transform: uppercase; }
.tc-swatches { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.tc-swatch {
  width: 30px; height: 30px; border-radius: 50%; cursor: pointer;
  border: 2px solid transparent; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.15);
  transition: transform var(--gym-dur) var(--gym-ease-spring);
}
.tc-swatch:hover { transform: scale(1.12); }
.tc-swatch.active { border-color: var(--gym-text); transform: scale(1.12); }

.theme-preview { display: flex; flex-direction: column; gap: 0.5rem; }
.tp-label { font-size: 0.78rem; color: var(--gym-text-faint); text-transform: uppercase; letter-spacing: 0.05em; }
.tp-row { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.tp-pill {
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: var(--gym-success-soft); color: var(--gym-success);
  border: 1px solid color-mix(in srgb, var(--gym-success) 45%, transparent);
  border-radius: var(--gym-radius-pill); padding: 0.3rem 0.7rem; font-size: 0.8rem; font-weight: 700;
}

/* ── Profile ──────────────────────────────────────────────────────── */
.profile-row { display: flex; justify-content: space-between; padding: 0.6rem 0; border-bottom: 1px solid var(--gym-border); }
.profile-label { color: var(--gym-text-muted); font-size: 0.88rem; }
.profile-value { color: var(--gym-text); font-weight: 600; font-size: 0.9rem; }
.bw-row { display: flex; gap: 0.5rem; }
.bw-input {
  flex: 1; padding: 0.6rem 0.8rem; border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-sm); font-size: 0.95rem; background: var(--gym-surface-2); color: var(--gym-text);
}

/* ── Body measurements ── */
.bm-form { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.bm-form .bw-input { flex: 1 1 90px; min-width: 90px; }
.bm-history { margin-top: 0.5rem; overflow-x: auto; }
.bm-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.bm-table th, .bm-table td { text-align: left; padding: 0.45rem 0.6rem; border-bottom: 1px solid var(--gym-border); white-space: nowrap; }
.bm-table th { color: var(--gym-text-dim); font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; }
.bm-actions { display: flex; gap: 0.6rem; }
.btn-danger { color: #e06666; }

/* ── Exercise capability flags + MET ──────────────────────────────── */
.met-row { display: flex; align-items: center; gap: 0.6rem; }
.met-input { width: 90px; padding: 0.5rem 0.6rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 0.9rem; background: var(--gym-surface-2); color: var(--gym-text); }
.flags-box { display: flex; flex-wrap: wrap; gap: 0.5rem 0.9rem; padding: 0.6rem; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); }
.flag-label { display: flex; align-items: center; gap: 0.35rem; font-size: 0.85rem; color: var(--gym-text-muted); cursor: pointer; }

/* ── Cards ────────────────────────────────────────────────────────── */
.card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem; box-shadow: var(--gym-shadow-sm); }
.card h3 {
  margin: 0 0 1rem; font-size: 1.02rem; color: var(--gym-text); font-weight: 700;
  border-bottom: 1px solid var(--gym-border); padding-bottom: 0.65rem;
}

/* ── Inputs / forms ───────────────────────────────────────────────── */
.input-group { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
.input-group label { font-size: 0.85rem; font-weight: 600; color: var(--gym-text-muted); }
.input-group input, .input-group select, .input-group textarea, .url-input,
.inline-form input, .stacked-form input, .stacked-form select, .stacked-form textarea {
  padding: 0.6rem 0.8rem; border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-sm); font-size: 0.92rem; background: var(--gym-surface-2); color: var(--gym-text);
  transition: border-color var(--gym-dur) var(--gym-ease), box-shadow var(--gym-dur) var(--gym-ease);
}
.input-group input::placeholder, .url-input::placeholder,
.inline-form input::placeholder, .stacked-form input::placeholder, .stacked-form textarea::placeholder { color: var(--gym-text-faint); }
.input-group input:focus, .input-group select:focus,
.input-group textarea:focus, .url-input:focus,
.inline-form input:focus, .stacked-form input:focus, .stacked-form select:focus, .stacked-form textarea:focus,
.bw-input:focus, .met-input:focus, .edit-select:focus {
  outline: none; border-color: var(--gym-accent);
  box-shadow: 0 0 0 3px var(--gym-accent-soft);
}

.input-row { display: flex; gap: 1rem; flex-wrap: wrap; }
.input-row .input-group { flex: 1; min-width: 90px; margin-bottom: 0.85rem; }
.flex-2 { flex: 2 !important; }
.small-label { font-size: 0.8rem; font-weight: 600; color: var(--gym-text-muted); margin-bottom: 0.25rem; }

.inline-form { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
.inline-form input { flex: 1; }
.stacked-form { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }

.multi-select-box {
  max-height: 130px; overflow-y: auto;
  border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); padding: 0.5rem; background: var(--gym-surface-2);
}
.multi-select-sm { max-height: 90px; }
.checkbox-label {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.2rem 0; font-size: 0.88rem; color: var(--gym-text); cursor: pointer;
}
.flag-label input, .checkbox-label input { accent-color: var(--gym-accent); }

/* ── Data lists ───────────────────────────────────────────────────── */
.data-list { list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--gym-border); padding-top: 0.5rem; }
.data-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.5rem 0; font-size: 0.9rem; color: var(--gym-text); border-bottom: 1px solid var(--gym-border); gap: 0.5rem;
}
.data-item:last-child { border-bottom: none; }
.empty-item { color: var(--gym-text-faint); font-style: italic; font-size: 0.85rem; }

.edit-input {
  flex: 1; padding: 0.4rem 0.55rem;
  border: 1px solid var(--gym-accent); border-radius: var(--gym-radius-sm); font-size: 0.88rem;
  background: var(--gym-surface-2); color: var(--gym-text);
}
.edit-select { padding: 0.4rem 0.55rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 0.88rem; background: var(--gym-surface-2); color: var(--gym-text); }
.edit-row { display: flex; gap: 0.4rem; flex: 1; }
.edit-col { display: flex; flex-direction: column; gap: 0.3rem; flex: 1; }

.action-btns { display: flex; gap: 0.25rem; flex-shrink: 0; }
.btn-edit, .btn-delete, .btn-save, .btn-cancel {
  background: none; border: none; cursor: pointer; color: var(--gym-text-muted);
  padding: 0.3rem 0.45rem; border-radius: var(--gym-radius-sm); font-size: 0.85rem; transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease);
}
.btn-edit:hover { background: var(--gym-accent-soft); color: var(--gym-accent); }
.btn-delete:hover { background: var(--gym-danger-soft); color: var(--gym-danger); }
.btn-save:hover { background: var(--gym-success-soft); color: var(--gym-success); }
.btn-cancel:hover { background: var(--gym-surface-3); }

.tag {
  font-size: 0.75rem; background: var(--gym-accent-soft); color: var(--gym-accent);
  padding: 0.15rem 0.55rem; border-radius: var(--gym-radius-pill); margin-left: 0.4rem;
}

/* ── Lift list ────────────────────────────────────────────────────── */
.lift-list { list-style: none; padding: 0; margin: 0; }
.lift-item {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 0.8rem 0; border-bottom: 1px solid var(--gym-border); gap: 1rem;
}
.lift-item:last-child { border-bottom: none; }
.lift-left { display: flex; flex-direction: column; gap: 0.2rem; }
.lift-right { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
.lift-exercise { font-size: 0.95rem; color: var(--gym-text); }
.lift-stats { font-size: 0.88rem; color: var(--gym-text-muted); }
.lift-notes { font-size: 0.8rem; color: var(--gym-text-faint); font-style: italic; }
.lift-date {
  font-size: 0.78rem; color: var(--gym-text-muted); background: var(--gym-surface-2);
  padding: 0.25rem 0.55rem; border-radius: var(--gym-radius-sm); white-space: nowrap;
}
.btn-danger-sm {
  background: none; border: none; color: var(--gym-text-faint); cursor: pointer;
  font-size: 0.85rem; padding: 0.2rem 0.4rem; border-radius: var(--gym-radius-sm);
  transition: color var(--gym-dur) var(--gym-ease), background var(--gym-dur) var(--gym-ease);
}
.btn-danger-sm:hover { color: var(--gym-danger); background: var(--gym-danger-soft); }

.empty-state { color: var(--gym-text-faint); font-size: 0.9rem; padding: 1rem 0; }

/* ── PB table ─────────────────────────────────────────────────────── */
.lift-status { font-size: 0.78rem; font-weight: 700; padding: 0.12rem 0.5rem; border-radius: var(--gym-radius-pill); width: fit-content; }
.lift-status.competition { background: var(--gym-warning-soft); color: var(--gym-warning); }
.lift-status.excluded { background: var(--gym-danger-soft); color: var(--gym-danger); }
.btn-icon-sm { background: none; border: 1px solid var(--gym-border); color: var(--gym-text-muted); border-radius: var(--gym-radius-sm); width: 30px; height: 30px; cursor: pointer; font-size: 0.9rem; }
.btn-icon-sm:hover { border-color: var(--gym-accent-border); background: var(--gym-accent-softer); }

/* ── Buttons ──────────────────────────────────────────────────────── */
.btn-primary {
  background: var(--gym-accent); color: var(--gym-on-accent); border: none;
  padding: 0.6rem 1.3rem; border-radius: var(--gym-radius-sm);
  font-size: 0.92rem; font-weight: 700; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease), box-shadow var(--gym-dur) var(--gym-ease);
}
.btn-primary:hover:not(:disabled) { background: var(--gym-accent-hover); box-shadow: var(--gym-shadow-accent); }
.btn-primary:active:not(:disabled) { transform: translateY(1px) scale(0.99); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary.full-width { width: 100%; }

.btn-secondary {
  background: var(--gym-surface-2); color: var(--gym-accent); border: 1.5px solid var(--gym-accent-border);
  padding: 0.55rem 1.15rem; border-radius: var(--gym-radius-sm);
  font-size: 0.92rem; font-weight: 700; cursor: pointer; transition: background var(--gym-dur) var(--gym-ease);
}
.btn-secondary:hover:not(:disabled) { background: var(--gym-accent-soft); }
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-outline-sm {
  background: none; border: 1px solid var(--gym-border-strong); color: var(--gym-text-muted);
  padding: 0.4rem 0.9rem; border-radius: var(--gym-radius-sm); font-size: 0.85rem; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease);
}
.btn-outline-sm:hover { background: var(--gym-surface-2); color: var(--gym-text); }

.btn-icon {
  background: var(--gym-surface-2); border: 1px solid var(--gym-border); cursor: pointer; font-size: 1.05rem;
  color: var(--gym-text-muted); width: 38px; height: 38px; border-radius: 11px;
  display: grid; place-items: center; transition: background var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease);
}
.btn-icon:hover { background: var(--gym-surface-3); color: var(--gym-text); }
.btn-icon:active { transform: scale(0.94); }

.btn-link {
  background: none; border: none; color: var(--gym-accent); cursor: pointer;
  font-size: 0.88rem; text-decoration: underline; padding: 0;
}

/* ── Toast ────────────────────────────────────────────────────────── */
.status-toast {
  position: fixed; left: 1rem; right: 1rem; bottom: calc(var(--gym-bottomnav-h) + 0.85rem);
  background: var(--gym-surface-3); color: var(--gym-text); border: 1px solid var(--gym-border-strong);
  padding: 0.75rem 1.1rem; text-align: center;
  border-radius: var(--gym-radius); font-size: 0.9rem; box-shadow: var(--gym-shadow); z-index: 1400;
}
.status-toast.toast-error { background: var(--gym-danger); color: #fff; border-color: var(--gym-danger); }
.toast-enter-active, .toast-leave-active { transition: opacity var(--gym-dur-lg) var(--gym-ease), transform var(--gym-dur-lg) var(--gym-ease); }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateY(12px); }

/* ── Responsive: desktop top-nav vs mobile bottom-nav ─────────────── */
@media (min-width: 768px) {
  .gym-tracker-container { padding: 1.5rem 1.5rem 2rem; }
}

@media (max-width: 767px) {
  .tab-nav { display: none; }
  .dash-actions { display: none; }
  .dash-header { margin-bottom: 1rem; }

  .bottom-nav {
    display: flex; position: fixed; left: 0; right: 0; bottom: 0; z-index: 850;
    background: color-mix(in srgb, var(--gym-surface) 92%, transparent);
    backdrop-filter: blur(12px);
    border-top: 1px solid var(--gym-border);
    padding: 0.3rem 0.3rem calc(0.3rem + env(safe-area-inset-bottom, 0px));
  }
  .bn-item {
    flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.15rem;
    background: none; border: none; cursor: pointer; padding: 0.45rem 0.2rem;
    color: var(--gym-text-faint); border-radius: var(--gym-radius-sm);
    transition: color var(--gym-dur) var(--gym-ease), background var(--gym-dur) var(--gym-ease);
  }
  .bn-icon { font-size: 1.3rem; line-height: 1; transition: transform var(--gym-dur) var(--gym-ease-spring); }
  .bn-label { font-size: 0.68rem; font-weight: 600; }
  .bn-item.active { color: var(--gym-accent); }
  .bn-item.active .bn-icon { transform: translateY(-2px) scale(1.08); }
  .bn-item:active .bn-icon { transform: scale(0.9); }
}
</style>
